import { lazy, Suspense } from 'react'
// Phase 26B / 60.S2: gates admin-only routes on can.viewPlatformAdmin.
//
// ROLE-GATE-1: ONE DENIAL BEHAVIOUR EVERYWHERE. This used to <Navigate> to
// /dashboard/quotes, so a signed-in non-admin who followed an admin link was
// silently bounced to an unrelated page with nothing explaining why — which
// reads as a broken link, not as a permission boundary. It now renders the
// same AccessDeniedNotice AuthGuard renders, so every refusal in the app
// looks and behaves identically. The `fallback` prop is kept and forwarded
// as the notice's back link so existing call sites keep their meaning.

import type { ReactNode } from 'react'
import { useAuth } from '../../hooks/useAuth'
import { can } from '../../lib/roles'
// FRONTDOOR-SPEED-1 — LAZY. AdminGuard is statically imported by App.tsx,
// so this refusal screen was in the entry chunk every stranger downloads.
const AccessDeniedNotice = lazy(() =>
  import('./AccessDeniedNotice').then((m) => ({ default: m.AccessDeniedNotice })),
)

interface AdminGuardProps {
  children: ReactNode
  fallback?: string
}

export function AdminGuard({ children, fallback }: AdminGuardProps) {
  const { profile, loading } = useAuth()

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-sm text-muted-foreground">Loading...</div>
      </div>
    )
  }

  if (!can.viewPlatformAdmin(profile)) {
    return (
      <Suspense fallback={null}>
        <AccessDeniedNotice
          message="This is a platform admin area. Ask your team owner if you need access."
          {...(fallback ? { backRoute: fallback } : {})}
        />
      </Suspense>
    )
  }

  return <>{children}</>
}
